import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Modal } from "@mui/material";
import { useParams } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import { UpfrontPaymentFinancialModalWrapper } from "./UpfrontPayment.styles";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import { CreateSetupIntent } from "./Services/UpfrontPaymentService";
import {
  ProfileModalTitle,
  ProfileModalTitleWrapper,
  ProfileModalContentWrapper,
  CenteredModalContainer,
} from "../shared/Modal/Modal.styles";
import { EditPaymentMethodSuccessConfirmation } from "./EditPaymentMethodSuccessConfirmation";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { ErrorPopup } from "../shared/ErrorPopup/ErrorPopup";
import { BillingSupportNumber } from "./types";
import {
  EditPaymentMethodForm,
  PaymentMethodInfo,
} from "./EditPaymentMethodForm";

const stripePromise = import.meta.env.VITE_UPFRONT_STRIPE_PK
  ? loadStripe(import.meta.env.VITE_UPFRONT_STRIPE_PK)
  : null;

interface EditPaymentMethodModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (paymentMethodInfo: PaymentMethodInfo) => void;
  reqIdentifier: string;
}

export const EditPaymentMethodModal: React.FC<EditPaymentMethodModalProps> = ({
  open,
  onClose,
  onSuccess,
  reqIdentifier,
}) => {
  const { deviceType } = useDeviceHook();
  const { t } = useTranslation();
  const { billingPK } = useParams<{ billingPK: string }>();

  const [clientSecret, setClientSecret] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [updatedPaymentMethod, setUpdatedPaymentMethod] =
    useState<PaymentMethodInfo | null>(null);

  const resetState = () => {
    setClientSecret(undefined);
    setErrorMessage(undefined);
    setUpdatedPaymentMethod(null);
  };

  // Create setup intent when modal opens
  useEffect(() => {
    if (open && billingPK && !clientSecret) {
      setLoading(true);
      resetState();

      TrackAnalyticsEvent("impression", undefined, reqIdentifier, {
        impression_type: "Modal",
        modal_name: "Edit Payment Method",
        page_name: "/client/upfront-payment",
      });

      CreateSetupIntent(billingPK)
        .then(async (response) => {
          if (response.ok) {
            const data = await response.json();
            setClientSecret(data.client_secret);
          } else {
            const errorData = await response.json();
            setErrorMessage(
              errorData.error ||
                t("upfrontPayment.editPaymentMethod.setupError", {
                  phone: BillingSupportNumber,
                }),
            );
          }
        })
        .catch(() => {
          setErrorMessage(
            t("upfrontPayment.editPaymentMethod.initializeError", {
              phone: BillingSupportNumber,
            }),
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, billingPK, clientSecret]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  // Prevent accidental page reload/navigation when modal is open with active session
  useEffect(() => {
    if (open && clientSecret) {
      // Prompt confirmation when reload page is triggered
      window.onbeforeunload = () => "";

      // Unmount the window.onbeforeunload event
      return () => {
        window.onbeforeunload = null;
      };
    }
  }, [open, clientSecret]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFormSuccess = (paymentMethodInfo: PaymentMethodInfo) => {
    setUpdatedPaymentMethod(paymentMethodInfo);
    onSuccess(paymentMethodInfo);
  };

  if (updatedPaymentMethod) {
    return (
      <Modal open={open} onClose={handleClose}>
        <CenteredModalContainer>
          <EditPaymentMethodSuccessConfirmation
            onPrimaryAction={handleClose}
            updatedPaymentMethod={updatedPaymentMethod}
          />
        </CenteredModalContainer>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      style={{
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
        pointerEvents: loading ? "none" : undefined,
      }}
    >
      <UpfrontPaymentFinancialModalWrapper
        deviceType={deviceType}
        allowScroll
        receipt={false}
      >
        <ProfileModalTitle deviceType={deviceType}>
          <ProfileModalTitleWrapper deviceType={deviceType}>
            <div>{t("upfrontPayment.editPaymentMethod.title")}</div>
            <CloseIcon onClick={handleClose} />
          </ProfileModalTitleWrapper>
        </ProfileModalTitle>
        <ProfileModalContentWrapper>
          {errorMessage && (
            <ErrorPopup
              message={errorMessage}
              hideMessage={() => setErrorMessage("")}
            />
          )}
          {loading && <SpinnerLoading />}

          {clientSecret && (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
              }}
            >
              <EditPaymentMethodForm
                clientSecret={clientSecret}
                onClose={handleClose}
                onSuccess={handleFormSuccess}
                reqIdentifier={reqIdentifier}
              />
            </Elements>
          )}
        </ProfileModalContentWrapper>
      </UpfrontPaymentFinancialModalWrapper>
    </Modal>
  );
};
